import { useCallback, useEffect, useRef, useState } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import SalesOrderLineInterface from '@/interfaces/SalesOrderLineInterface';
import PlaceholderImage from '@/assets/images/placeholder.svg';
import { X } from 'lucide-react';
import ArrowRight from '../../../assets/icons/arrow-right.svg?react';

interface ReviewModalProps {
  open: boolean;
  lines: SalesOrderLineInterface[];
  onGoBack: () => void;
  onConfirm: () => void;
}

const getProductTitle = (line: SalesOrderLineInterface) =>
  line.Sold_As_Description || line.Description || 'Product awaiting details';

const getLineName = (line: SalesOrderLineInterface) =>
  line.Sold_As_Description || line.Description || 'Product awaiting details';

// const getLineCode = (line: SalesOrderLineInterface) => line.Sold_as_SKU || line.Item_No_2 || line.No;

const getQuantity = (line: SalesOrderLineInterface) => {
  const quantity = Number.parseFloat(line.Quantity);
  return Number.isFinite(quantity) ? quantity : 0;
};

const BOTTOM_SANITY_OFFSET = 100; // don't force them to scroll to the very bottom
const MIN_DISTANCE_FROM_BOTTOM = 20; // no need to scroll if there's just a little

export default function ReviewModal({ open, lines = [], onGoBack, onConfirm }: ReviewModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

  const updateScrollState = useCallback((container: HTMLDivElement) => {
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const hasReachedBottom = distanceFromBottom <= BOTTOM_SANITY_OFFSET;

    if (hasReachedBottom || distanceFromBottom <= MIN_DISTANCE_FROM_BOTTOM) setNextButtonDisabled(false);
  }, []);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;

    if (node) {
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
      if (distanceFromBottom <= MIN_DISTANCE_FROM_BOTTOM) setNextButtonDisabled(false);
    }
  }, []);

  useEffect(() => {
    setNextButtonDisabled(true);
    if (open === false) return;

    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;

      if (!container) return;
      if (container.scrollHeight <= container.clientHeight) setNextButtonDisabled(false);
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <AlertDialogPrimitive.Root open={open}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px]" />

        <AlertDialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[51] flex h-[calc(100dvh_-_16px)] max-h-[900px] w-[calc(100%_-_16px)] max-w-[540px] sm:max-w-[1200px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden overscroll-contain border border-border bg-background shadow-[0_18px_50px_rgba(10,10,10,0.22)] outline-none sm:h-[calc(100dvh_-_32px)] sm:w-[calc(100%_-_32px)]">
          <header className="relative flex shrink-0 items-center justify-center border-b border-dashed border-[#e5e5e5] px-[54px] py-[18px] text-center sm:px-[70px] sm:py-[22px]">
            <AlertDialogPrimitive.Title className="!m-0 text-[26px] font-bold leading-[31px] sm:text-[32px] sm:leading-[37px]">
              Review your products
            </AlertDialogPrimitive.Title>

            <button
              aria-label="Close review"
              className="absolute right-[14px] top-1/2 flex h-[36px] w-[36px] -translate-y-1/2 items-center justify-center text-screwfix-blue-shade-3 hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 sm:right-[20px]"
              onClick={onGoBack}
              type="button"
            >
              <X aria-hidden="true" className="h-[22px] w-[22px]" />
            </button>
          </header>

          <AlertDialogPrimitive.Description asChild>
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[14px] py-[18px] [font-family:archivo_narrowregular,sans-serif] [scrollbar-gutter:stable] sm:px-[38px] sm:py-[24px]"
              ref={setContainerRef}
              onScroll={(event) => updateScrollState(event.currentTarget)}
            >
              {lines.length > 0 ? (
                <ul aria-label="Products to review" className="divide-y divide-dashed divide-[#d1d1d1]">
                  {lines
                    .sort((a, b) => a.External_Document_Line_No - b.External_Document_Line_No)
                    .map((line) => {
                      const productTitle = getProductTitle(line);
                      const lineName = getLineName(line);

                      return (
                        <li className="py-[24px] first:pt-0 last:pb-0" key={line.Line_No}>
                          <figure className="mx-auto flex w-full max-w-[430px] sm:max-w-[600px] flex-col items-center">
                            <div className="aspect-square w-full bg-muted/50 p-[12px] sm:p-[18px] relative">
                              <img
                                alt={productTitle}
                                className="h-full w-full object-contain"
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src = PlaceholderImage;
                                }}
                                src={line.Product_URL_3 || PlaceholderImage}
                              />
                            </div>
                            <figcaption className="mt-[14px] flex w-full flex-col items-center gap-[8px] px-[4px] text-center">
                              <h3 className="line-clamp-2 text-[22px] font-light leading-[26px] sm:text-[20px] sm:leading-[23px]">
                                <b className="font-bold">{getQuantity(line)}x</b> {lineName}
                              </h3>
                            </figcaption>
                          </figure>
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p className="!m-0 py-[28px] text-center text-[clamp(16px,calc(14px_+_0.3vw),17px)] font-normal leading-[21px] text-muted-foreground">
                  There are no products to review.
                </p>
              )}
            </div>
          </AlertDialogPrimitive.Description>

          <footer className="flex shrink-0 flex-row gap-[10px] border-t border-dashed border-[#e5e5e5] px-[14px] py-[14px] sm:gap-[20px] sm:px-[38px] sm:py-[20px]">
            <AlertDialogPrimitive.Cancel asChild>
              <button
                className="button-screwfix-secondary flex h-[48px] min-w-0 flex-1 items-center justify-center !px-[10px] [font-family:archivo_narrowbold,sans-serif] !text-[17px] font-bold focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-60 sm:!px-[22px] sm:!text-[19px]"
                onClick={onGoBack}
                type="button"
              >
                Cancel
              </button>
            </AlertDialogPrimitive.Cancel>

            <AlertDialogPrimitive.Action asChild>
              <button
                className="inline-flex w-full min-h-[42px] flex-1 items-center justify-start gap-[12px] rounded-[6px] bg-blue-600 px-[18px] text-[16px] font-bold button-screwfix-success has-icon"
                disabled={nextButtonDisabled}
                onClick={onConfirm}
                type="button"
              >
                <div className="flex flex-1 items-center justify-center">Next</div>
                <ArrowRight className="h-[1em] w-[1em] fill-white " />
              </button>
            </AlertDialogPrimitive.Action>
          </footer>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
